/**
 * AppAiChatHttp bond setup
 *
 * Wires @molecule/app-ai-chat-http to @molecule/app-ai-chat.
 *
 * The provider's sendMessage is wrapped so an AI daily-budget limit hit — which
 * the backend returns as a 429 ONLY on the chat route, surfaced by the chat
 * provider's own fetch as a `{ type: 'error', limitType: 'ai_cost' | 'ai_messages' }`
 * stream event, NOT through the @molecule/app-http error interceptor — still fires
 * the same usage signals: record the daily-limit hit (for the returning-session
 * reset banner) and invalidate the usage rollup so the budget banner appears
 * immediately instead of only after a page refresh (mvp G2).
 */

import { setProvider } from '@molecule/app-ai-chat'
import { createProvider } from '@molecule/app-ai-chat-http'

import { API_BASE_URL } from '../config.js'
import { notifyUsageInvalidated } from '../utilities/usage-utilities.js'
import { recordDailyLimitHit } from './app-http.js'

/** Daily AI caps whose chat-stream error should refresh the usage banner. */
const AI_DAILY_LIMIT_TYPES = new Set(['ai_cost', 'ai_messages'])

/**
 * The user's own monthly extra-usage cap (`limitType: 'spend_cap'`). Its
 * chat-stream 429 must refresh the usage rollup (spend just changed) but is
 * NOT a daily-budget hit — recording it would wrongly arm the daily
 * budget-reset welcome-back banner.
 */
const SPEND_CAP_LIMIT_TYPE = 'spend_cap'

/**
 * The prepaid usage balance ran dry (`limitType: 'usage_balance'`). Like the
 * spend cap it pauses metered usage, but the fix is adding funds rather than
 * raising a ceiling — see `upgrade-copy.ts:balanceEmptyLine`.
 */
const USAGE_BALANCE_LIMIT_TYPE = 'usage_balance'

export function setupAppAiChatHttp(): void {
  const provider = createProvider({ baseUrl: API_BASE_URL })
  const originalSendMessage = provider.sendMessage.bind(provider)
  provider.sendMessage = (message, config, onEvent, attachments) =>
    originalSendMessage(
      message,
      config,
      (event) => {
        if (event.type === 'error' && typeof event.limitType === 'string') {
          if (AI_DAILY_LIMIT_TYPES.has(event.limitType)) {
            // This 429 travels the chat stream (raw fetch), bypassing the app-http
            // interceptor — so fire the same usage signals here (mvp G2).
            recordDailyLimitHit()
            notifyUsageInvalidated()
          } else if (
            event.limitType === SPEND_CAP_LIMIT_TYPE ||
            event.limitType === USAGE_BALANCE_LIMIT_TYPE
          ) {
            // Spend-cap pause or an empty prepaid balance: refresh the usage
            // rollup only (no daily-hit record — neither resets on a timer, so
            // suppressing autonomous retries on a daily-budget schedule would be
            // wrong). The balance figure changing is exactly what the user needs
            // to see, so the rollup refresh matters here.
            notifyUsageInvalidated()
          }
        }
        onEvent(event)
      },
      attachments,
    )
  setProvider(provider)
}
